/**
 * Hook for real-time candle updates via unified SSE
 *
 * Connects to the unified SSE endpoint for live ES candle streaming.
 * Uses the shared UnifiedSSEClient singleton to avoid per-indicator connections.
 *
 * =============================================================================
 * USAGE
 * =============================================================================
 *
 * const { connected, error } = useLiveCandles(
 *   (candle, isComplete) => {
 *     // isComplete=true: Candle period finished (e.g., 5-minute mark)
 *     // isComplete=false: In-progress update (real-time price movement)
 *     chart.series.update(candle);
 *   },
 *   (candles) => {
 *     // Bulk historical candles received on initial connection
 *     chart.series.setData(candles);
 *   },
 *   '5m' // timeframe
 * );
 *
 * =============================================================================
 * SSE EVENT TYPES
 * =============================================================================
 *
 * 1. bulk_candles - Sent immediately on connection
 *    { timeframe: '5m', data: [...candles] }
 *
 * 2. candle - Completed candle (end of period)
 *    { timeframe: '5m', data: {time, open, high, low, close, volume, buy_volume, trades} }
 *
 * 3. current_candle - In-progress candle update (every ~10 trades)
 *    { timeframe: '5m', data: same shape as candle }
 *
 * All three carry the same wire candle shape as REST /api/es/candles;
 * consumers derive sell_volume = volume - buy_volume (CVDIndicator does).
 *
 * =============================================================================
 */

import { useEffect, useRef, useState } from 'react';
import { unifiedSSE } from './useUnifiedSSE';

/**
 * Hook for live candle updates via unified SSE
 *
 * @param {Function} onCandleUpdate - Callback for candle updates (candle, isComplete)
 * @param {Function} onBulkCandles - Callback for bulk historical candles
 * @param {string} timeframe - Desired timeframe (e.g., '5m')
 * @param {boolean} enabled - Whether to connect (default true)
 * @returns {Object} - Hook state and methods
 */
export function useLiveCandles(onCandleUpdate, onBulkCandles, timeframe = '5m', enabled = true) {
  const onCandleUpdateRef = useRef(onCandleUpdate);
  const onBulkCandlesRef = useRef(onBulkCandles);
  const bulkReceivedRef = useRef(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    onCandleUpdateRef.current = onCandleUpdate;
    onBulkCandlesRef.current = onBulkCandles;
  }, [onCandleUpdate, onBulkCandles]);

  useEffect(() => {
    if (!enabled) {
      setConnected(false);
      return;
    }

    const channel = `es:${timeframe}`;
    bulkReceivedRef.current = false;

    const handleBulk = (message) => {
      if (onBulkCandlesRef.current && Array.isArray(message.data)) {
        console.log('[LiveCandles] Received bulk candles:', message.data.length);
        bulkReceivedRef.current = true;
        onBulkCandlesRef.current(message.data);
        setConnected(true);
        setError(null);
      }
    };

    const handleCandle = (message) => {
      if (onCandleUpdateRef.current && message.data && bulkReceivedRef.current) {
        onCandleUpdateRef.current(message.data, true);
      }
    };

    const handleCurrentCandle = (message) => {
      if (onCandleUpdateRef.current && message.data && bulkReceivedRef.current) {
        onCandleUpdateRef.current(message.data, false);
      }
    };

    const unsub1 = unifiedSSE.subscribe(channel, 'bulk_candles', handleBulk);
    const unsub2 = unifiedSSE.subscribe(channel, 'candle', handleCandle);
    const unsub3 = unifiedSSE.subscribe(channel, 'current_candle', handleCurrentCandle);

    console.log(`[LiveCandles] Subscribed to unified SSE channel: ${channel}`);

    return () => {
      unsub1();
      unsub2();
      unsub3();
      setConnected(false);
      console.log(`[LiveCandles] Unsubscribed from unified SSE channel: ${channel}`);
    };
  }, [timeframe, enabled]);

  return { connected, error };
}

export default useLiveCandles;
